<template>
  <div class="grid gap-6">
    <div
      :class="{
        'divide-y rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-500': grouped,
        'grid gap-6': !grouped
      }"
    >
      <YapInput
        name="country"
        :schema="schema.properties.country"
        v-model="modelValue.country"
        :errors="validationErrors"
        :bold-label="boldLabels"
        :floating="floating"
        :grouped="grouped"
        :class="grouped ? 'rounded-t-lg' : ''"
        :required="!addressIsOptional && schema.required?.includes('country')"
        @blur="validateAddress"
      />
      <div class="grid grid-cols-4" :class="grouped ? 'divide-x dark:divide-yapdarkgray-500' : 'gap-3'">
        <YapInput
          name="street"
          :schema="schema.properties.street"
          v-model="modelValue.street"
          :errors="validationErrors"
          :bold-label="boldLabels"
          :floating="floating"
          :grouped="grouped"
          :required="!addressIsOptional && schema.required?.includes('street')"
          class="col-span-3"
          @blur="validateAddress"
        />
        <YapInput
          name="number"
          :schema="schema.properties.number"
          v-model="modelValue.number"
          :errors="validationErrors"
          :bold-label="boldLabels"
          :floating="floating"
          :grouped="grouped"
          :required="!addressIsOptional && schema.required?.includes('number')"
          class="col-span-1 flex-none"
          @blur="validateAddress"
        />
      </div>
      <div
        class="grid grid-cols-3"
        :class="grouped ? 'divide-x overflow-hidden rounded-b-lg dark:divide-yapdarkgray-500' : 'gap-3'"
      >
        <YapInput
          name="postalCode"
          :schema="schema.properties.postalCode"
          v-model="modelValue.postalCode"
          :errors="validationErrors"
          :bold-label="boldLabels"
          :floating="floating"
          :grouped="grouped"
          :required="!addressIsOptional && schema.required?.includes('postalCode')"
          class="col-span-1"
          @change="handleZipChange"
          @blur="validateAddress"
        />
        <YapInput
          name="city"
          :schema="state.customSchema?.properties.city ?? schema.properties.city"
          v-model="modelValue.city"
          :errors="validationErrors"
          :bold-label="boldLabels"
          :floating="floating"
          :grouped="grouped"
          :required="!addressIsOptional && schema.required?.includes('city')"
          class="col-span-2"
          @blur="validateAddress"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';

import { AddressV2, Schema } from '@/types/general';
import YapInput from '@/components/form/YapInput.vue';
import useAddressValidation from '@/composables/useAddressValidation';
import { YapInputValidationError } from '@/components/form/types/yapInputValidationError';

const modelValue = defineModel<AddressV2>({ required: true });

const props = defineProps<{
  validationErrors?: Array<YapInputValidationError>;
  schema: Schema;
  floating?: boolean;
  grouped?: boolean;
  boldLabels?: boolean;
  addressIsOptional?: boolean;
}>();

interface State {
  preValidated: boolean;
  addressHasEmptyFields: boolean;
  customSchema?: Schema;
}
const state: State = reactive({
  preValidated: false,
  addressHasEmptyFields: computed(
    () =>
      !(
        !!modelValue.value.country &&
        !!modelValue.value.street &&
        !!modelValue.value.number &&
        !!modelValue.value.postalCode &&
        !!modelValue.value.city
      )
  ),
  customSchema: undefined
});

const { addressValidationV2 } = useAddressValidation();
async function validateAddress() {
  if (!state.addressHasEmptyFields && !state.preValidated) {
    const fixed = await addressValidationV2(modelValue.value);
    if (fixed) {
      modelValue.value = fixed;
    }
    state.preValidated = true;
  }
}

const { zipValidation } = useAddressValidation();

async function handleZipChange(event: Event) {
  const zipInput = event.target as HTMLInputElement;
  const response = await zipValidation(zipInput.value, modelValue.value.country);
  if (response?.city) {
    state.customSchema = undefined;
    modelValue.value.city = response.city;
  } else if (response?.cities) {
    state.customSchema = {
      ...props.schema,
      properties: {
        town: {
          ...props.schema.properties.city,
          enum: response.cities
        }
      }
    };
    modelValue.value.city = response.cities[0];
  } else {
    // Handle the error case
    console.error('Error fetching city data:', response);
  }
}
</script>
